import React, { useMemo } from "react"
import Link from "next/link"
import { useRouter } from "next/router"

import {
  Button,
  Card,
  CardContent,
  CopyableId,
  DrawerBody,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  Skeleton,
} from "@coval/ui"
import { ExternalLink } from "lucide-react"

import { useQueryParams } from "@/hooks/useQueryParams"

import { TEST_CASE_DEEP_LINK_PARAM } from "../editor/hooks/useTestCaseDeepLink"
import {
  useTestSetMetadataTransport,
  useTestSetRecordsTransport,
  useTestSetVersionsTransport,
} from "../transport"
import {
  hasTestCaseDetails,
  readScriptTurns,
  TestCaseDetails,
  type TestCaseData,
} from "./TestCaseDetails"

interface TestCaseDrawerPanelProps {
  testSetId: string
  /** The test case as the conversation that opened this drawer ran it. */
  testCase: TestCaseData
  /**
   * The test set version the run pinned. The test set page only shows the
   * current version, so the link says so rather than pass for what ran.
   */
  testSetVersionId?: string
}

/** The test set page, scrolled to this test case's row when it has an id. */
function testSetPath(testSetId: string, testCaseId?: string) {
  const path = `/test-sets/${testSetId}`
  return testCaseId
    ? `${path}?${new URLSearchParams({ [TEST_CASE_DEEP_LINK_PARAM]: testCaseId })}`
    : path
}

/**
 * The script turns of the stored test case a conversation ran. A conversation
 * records its case's input but not a Script case's turns, so they are read from
 * the test set: from the version the run pinned, which the version history
 * lists even while it is the current one, else from the live test set for a
 * run that pinned none. A pinned version missing from the history is gone, and
 * the live turns may not be what ran, so none are shown.
 */
function useStoredScriptTurns(
  testSetId: string,
  testCaseId: string | undefined,
  testSetVersionId: string | undefined,
) {
  const enabled = Boolean(testCaseId)
  const versionsQuery = useTestSetVersionsTransport(testSetId, {
    enabled: enabled && Boolean(testSetVersionId),
  })
  const recordsQuery = useTestSetRecordsTransport(
    { test_set_id: testSetId },
    { enabled: enabled && !testSetVersionId },
  )
  const activeQuery = testSetVersionId ? versionsQuery : recordsQuery
  const testSetVersion = testSetVersionId
    ? versionsQuery.data?.versions.find(
        (version) => version.ulid === testSetVersionId,
      )
    : undefined

  const scriptTurns = useMemo(() => {
    const rows: unknown[] | undefined = testSetVersionId
      ? testSetVersion?.test_cases
      : recordsQuery.data
    const row = rows?.find(
      (candidate): candidate is Record<string, unknown> =>
        typeof candidate === "object" &&
        candidate !== null &&
        (candidate as Record<string, unknown>).id === testCaseId,
    )
    return readScriptTurns(row)
  }, [recordsQuery.data, testCaseId, testSetVersion, testSetVersionId])

  return {
    scriptTurns,
    error: enabled ? activeQuery.error : null,
    isLoading: enabled && activeQuery.isLoading,
    isVersionUnavailable:
      enabled &&
      Boolean(testSetVersionId) &&
      versionsQuery.isSuccess &&
      !testSetVersion,
    refetch: () => void activeQuery.refetch(),
  }
}

/**
 * One test case's read-only detail view, as the body of a drawer the caller
 * owns. It shows only the case a conversation ran, as it ran it, rather than
 * every case in its test set; the link under the title opens the full test set
 * in a new tab at this case's row.
 */
export const TestCaseDrawerPanel: React.FC<TestCaseDrawerPanelProps> = ({
  testSetId,
  testCase,
  testSetVersionId,
}) => {
  const router = useRouter()
  const queryParams = useQueryParams()
  const organizationSlug =
    typeof router.query.slug === "string" ? router.query.slug : undefined
  const metadataQuery = useTestSetMetadataTransport(testSetId)
  const testSetName = metadataQuery.data?.metadata?.display_name
  const storedScript = useStoredScriptTurns(
    testSetId,
    testCase.id,
    testSetVersionId,
  )
  // What the run recorded wins; the stored turns only fill in what it lacks.
  const shownTestCase = useMemo(
    () =>
      testCase.scriptTurns?.length || !storedScript.scriptTurns
        ? testCase
        : { ...testCase, scriptTurns: storedScript.scriptTurns },
    [storedScript.scriptTurns, testCase],
  )
  // A case with an input is not a Script case, so it never waits on the turns.
  const awaitsScript = !testCase.inputStr?.trim() && !shownTestCase.scriptTurns
  const linkLabel = testSetVersionId
    ? "View in current test set"
    : "View in test set"

  return (
    <>
      <DrawerHeader className="items-start">
        <DrawerTitle>Test case</DrawerTitle>
        <DrawerDescription className="flex flex-wrap items-center gap-1">
          {/* A cached name stays up through a failed background refetch; the
              retry sits beside it rather than in its place. */}
          {testSetName ? (
            <span>{testSetName}</span>
          ) : metadataQuery.error ? (
            <span>Couldn't load the test set.</span>
          ) : metadataQuery.isLoading ? (
            <span>Loading test set…</span>
          ) : (
            <span>Test set not found.</span>
          )}
          {metadataQuery.error ? (
            <Button variant="link" onClick={() => void metadataQuery.refetch()}>
              Retry
            </Button>
          ) : null}
          {testCase.id ? (
            <>
              <span aria-hidden>·</span>
              <span>ID:</span>
              <CopyableId id={testCase.id} label="Copy test case ID" />
            </>
          ) : null}
        </DrawerDescription>
        {/* Every resource page is scoped to an organization, so the link waits
            for the route to name one rather than resolve against another. */}
        {organizationSlug ? (
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button asChild variant="secondary" className="shrink-0">
              <Link
                href={queryParams.getHref(
                  testSetPath(testSetId, testCase.id),
                  organizationSlug,
                )}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${linkLabel} (opens in a new tab)`}
              >
                <ExternalLink className="h-4 w-4" />
                {linkLabel}
              </Link>
            </Button>
          </div>
        ) : null}
      </DrawerHeader>
      <DrawerBody className="space-y-3">
        {awaitsScript && storedScript.isLoading ? (
          <div role="status" aria-label="Loading test case details">
            <Skeleton className="h-10 w-full" />
          </div>
        ) : awaitsScript && storedScript.error ? (
          <p className="flex flex-wrap items-center gap-1 text-coval-text-muted text-sm">
            <span>Couldn't load this test case's details.</span>
            <Button variant="link" onClick={storedScript.refetch}>
              Retry
            </Button>
          </p>
        ) : awaitsScript && storedScript.isVersionUnavailable ? (
          <p className="text-coval-text-muted text-sm">
            The test set version used by this conversation is no longer
            available.
          </p>
        ) : null}
        {hasTestCaseDetails(shownTestCase) ? (
          <Card>
            <CardContent className="space-y-3 pt-4">
              <TestCaseDetails testCase={shownTestCase} />
            </CardContent>
          </Card>
        ) : awaitsScript &&
          (storedScript.isLoading ||
            storedScript.error ||
            storedScript.isVersionUnavailable) ? null : (
          <p className="text-coval-text-muted text-sm">
            No test case details were recorded for this conversation.
          </p>
        )}
      </DrawerBody>
    </>
  )
}
